/**
 * Mail Template Service
 * Handles API requests related to mail template management
 */
import { createService } from './serviceFactory';

// Define the base URL for mail template service
const baseUrl = '/api/supplier/app-mail-template';

// Create base service with standard CRUD operations
const baseService = createService(baseUrl);

const mailTemplateService = {
  // Include all methods from the base service
  ...baseService,
  
  /**
   * Get mail template by ID
   * @param {string} id - Mail template ID
   * @returns {Promise} - Response with mail template details
   */
  getMailTemplateById(id) {
    if (!id) {
      console.error('getMailTemplateById: id is required');
      return Promise.reject(new Error('Mail template ID is required'));
    }
    
    return baseService.client.get(`${baseUrl}/${id}`)
      .then(response => response.data);
  },

  /**
   * Create new mail template
   * @param {Object} templateData - Mail template data
   * @returns {Promise} - Response with created mail template
   */
  createMailTemplate(templateData) {
    if (!templateData) {
      console.error('createMailTemplate: templateData is required');
      return Promise.reject(new Error('Mail template data is required'));
    }
    
    return baseService.client.post(`${baseUrl}`, templateData)
      .then(response => response.data);
  },

  /**
   * Update mail template
   * @param {string} id - Mail template ID
   * @param {Object} templateData - Mail template data
   * @returns {Promise} - Response with updated mail template
   */
  updateMailTemplate(id, templateData) {
    if (!id) {
      console.error('updateMailTemplate: id is required');
      return Promise.reject(new Error('Mail template ID is required'));
    }
    
    if (!templateData) {
      console.error('updateMailTemplate: templateData is required');
      return Promise.reject(new Error('Mail template data is required'));
    }
    
    return baseService.client.put(`${baseUrl}/${id}`, templateData)
      .then(response => response.data);
  },

  /**
   * Delete mail template
   * @param {string} id - Mail template ID
   * @returns {Promise} - Response confirmation
   */
  deleteMailTemplate(id) {
    if (!id) {
      console.error('deleteMailTemplate: id is required');
      return Promise.reject(new Error('Mail template ID is required'));
    }
    
    return baseService.client.delete(`${baseUrl}/${id}`)
      .then(response => response.data);
  },

  /**
   * Search mail templates with pagination
   * @param {Object} searchParams - Search parameters
   * @returns {Promise} - Response with search results
   */
  searchMailTemplates(searchParams = {}) {
    const defaultParams = {
      searchText: '',
      page: 0,
      pageSize: 20,
      sorting: ''
    };
    
    const params = { ...defaultParams, ...searchParams };
    
    return baseService.client.post(`${baseUrl}/search`, params)
      .then(response => response.data);
  },

  /**
   * Get mail template by key
   * @param {string} tenantId - Tenant ID
   * @param {string} templateKey - Template key
   * @param {boolean} includeHostFallback - Include host fallback
   * @returns {Promise} - Response with mail template
   */
  getMailTemplateByKey(tenantId, templateKey, includeHostFallback = true) {
    if (!tenantId) {
      console.error('getMailTemplateByKey: tenantId is required');
      return Promise.reject(new Error('Tenant ID is required'));
    }
    
    return baseService.client.get(`${baseUrl}/by-key/${tenantId}`, {
      params: {
        templateKey,
        includeHostFallback
      }
    })
      .then(response => response.data);
  },

  /**
   * Restore mail template to original host template by key
   * @param {string} templateKey - Template key
   * @returns {Promise} - Response confirmation
   */
  restoreHostTemplateByKey(templateKey) {
    if (!templateKey) {
      console.error('restoreHostTemplateByKey: templateKey is required');
      return Promise.reject(new Error('Template key is required'));
    }
    
    return baseService.client.post(`${baseUrl}/restore-host-template-by-key`, null, {
      params: {
        templateKey
      }
    })
      .then(response => response.data);
  }
};

export default mailTemplateService;

